// Chemin: C:\gestion-copro\src\pages\admin\CoproprietePage.tsx
// Session 58 : Correction bug coproprietaireId → coproprieteIds[0]

import React, { useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import {
  getCopropriete,
  createCopropriete,
  updateCopropriete,
  getStatutGaranties
} from '../../services/copropriete.service';
import { Copropriete, TypeConstruction } from '../../types/copropriete.types';

const TYPES_CONSTRUCTION: { value: TypeConstruction; label: string }[] = [
  { value: 'neuf_vefa', label: 'Neuf VEFA' },
  { value: 'neuf_ccmi', label: 'Neuf CCMI' },
  { value: 'ancien', label: 'Ancien' },
  { value: 'renove', label: 'Rénové' }
];

export default function CoproprietePage() {
  const { firebaseUser, userProfile } = useAuth();
  const [copropriete, setCopropriete] = useState<Copropriete | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Formulaire
  const [formData, setFormData] = useState({
    nom: '',
    adresseRue: '',
    adresseCodePostal: '',
    adresseVille: '',
    nombreLots: 15,
    tantiemesTotal: 10000,
    typeConstruction: 'neuf_vefa' as TypeConstruction,
    dateReceptionTravaux: ''
  });

  useEffect(() => {
    loadCopropriete();
  }, [userProfile?.coproprieteIds?.[0]]);

  async function loadCopropriete() {
    try {
      setLoading(true);
      
      // Si l'utilisateur a une copropriété associée, la charger
      if (userProfile?.coproprieteIds?.[0]) {
        const data = await getCopropriete(userProfile.coproprieteIds?.[0]);
        if (data) {
          setCopropriete(data);
          setFormData({
            nom: data.nom || '',
            adresseRue: data.adresse?.rue || '',
            adresseCodePostal: data.adresse?.codePostal || '',
            adresseVille: data.adresse?.ville || '',
            nombreLots: data.nombreLots || 15,
            tantiemesTotal: data.tantiemesTotal || 10000,
            typeConstruction: data.typeConstruction || 'neuf_vefa',
            dateReceptionTravaux: data.dateReceptionTravaux
              ? data.dateReceptionTravaux.toDate().toISOString().split('T')[0]
              : ''
          });
          setIsCreating(false);
        } else {
          // L'ID existe mais pas le document → mode création
          setIsCreating(true);
        }
      } else {
        // Pas de copropriété associée → mode création
        setIsCreating(true);
      }
    } catch (err) {
      console.error('Erreur chargement copropriété:', err);
      setError('Impossible de charger les informations de la copropriété');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!firebaseUser?.uid) return;

    // Validation
    if (!formData.nom.trim()) {
      setError('Le nom de la copropriété est requis');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Générer un ID basé sur le nom (slugifié)
      const coproprieteId = formData.nom
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '')
        + '-' + Date.now().toString(36);

      // Créer la copropriété
      await createCopropriete(
        coproprieteId,
        {
          nom: formData.nom,
          adresse: {
            rue: formData.adresseRue,
            codePostal: formData.adresseCodePostal,
            ville: formData.adresseVille
          },
          nombreLots: formData.nombreLots,
          tantiemesTotal: formData.tantiemesTotal,
          typeConstruction: formData.typeConstruction,
          dateReceptionTravaux: formData.dateReceptionTravaux
            ? new Date(formData.dateReceptionTravaux)
            : undefined,
          abonnement: {
            plan: 'pro',
            dateDebut: new Date(),
            dateFin: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // +1 an
            statut: 'actif'
          }
        },
        firebaseUser.uid
      );

      // Associer la copropriété à l'utilisateur
      const userRef = doc(db, 'users', firebaseUser.uid);
      await updateDoc(userRef, {
        coproprieteIds: [coproprieteId]
      });

      setSuccess('Copropriété créée avec succès ! Rechargement...');
      
      // Recharger la page pour mettre à jour le contexte
      setTimeout(() => {
        window.location.reload();
      }, 1500);
      
    } catch (err) {
      console.error('Erreur création:', err);
      setError('Erreur lors de la création de la copropriété');
      setSaving(false);
    }
  }

  async function handleUpdate(e: React.FormEvent) {
    e.preventDefault();
    if (!userProfile?.coproprieteIds?.[0]) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await updateCopropriete(userProfile.coproprieteIds?.[0], {
        nom: formData.nom,
        adresse: {
          rue: formData.adresseRue,
          codePostal: formData.adresseCodePostal,
          ville: formData.adresseVille
        },
        nombreLots: formData.nombreLots,
        tantiemesTotal: formData.tantiemesTotal,
        typeConstruction: formData.typeConstruction,
        dateReceptionTravaux: formData.dateReceptionTravaux
          ? new Date(formData.dateReceptionTravaux)
          : undefined
      });

      setSuccess('Informations mises à jour avec succès');
      setEditMode(false);
      await loadCopropriete();
    } catch (err) {
      console.error('Erreur mise à jour:', err);
      setError('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  }

  function handleChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : value
    }));
  }

  function formatDate(timestamp: any): string {
    if (!timestamp) return 'Non renseigné';
    try {
      return timestamp.toDate().toLocaleDateString('fr-FR');
    } catch {
      return 'Non renseigné';
    }
  }

  function renderGarantieStatus(
    label: string,
    active: boolean,
    joursRestants: number | null,
    dateExpiration: any
  ) {
    let statusClass = 'bg-gray-100 text-gray-600';
    let statusText = 'Non définie';

    if (joursRestants !== null) {
      if (!active) {
        statusClass = 'bg-red-100 text-red-800';
        statusText = 'Expirée';
      } else if (joursRestants <= 30) {
        statusClass = 'bg-orange-100 text-orange-800';
        statusText = `${joursRestants} jours`;
      } else if (joursRestants <= 90) {
        statusClass = 'bg-yellow-100 text-yellow-800';
        statusText = `${joursRestants} jours`;
      } else {
        statusClass = 'bg-green-100 text-green-800';
        statusText = `${joursRestants} jours`;
      }
    }

    return (
      <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
        <div>
          <p className="font-medium text-gray-900">{label}</p>
          <p className="text-sm text-gray-500">
            {dateExpiration ? `Expire le ${formatDate(dateExpiration)}` : 'Date non renseignée'}
          </p>
        </div>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusClass}`}>
          {statusText}
        </span>
      </div>
    );
  }

  // Formulaire de création/édition
  function renderForm(isCreate: boolean) {
    return (
      <form onSubmit={isCreate ? handleCreate : handleUpdate} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nom de la copropriété *
          </label>
          <input
            type="text"
            name="nom"
            value={formData.nom}
            onChange={handleChange}
            placeholder="Ex: Résidence L'ORION"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Adresse
          </label>
          <input
            type="text"
            name="adresseRue"
            value={formData.adresseRue}
            onChange={handleChange}
            placeholder="Rue"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-2"
          />
          <div className="grid grid-cols-2 gap-2">
            <input
              type="text"
              name="adresseCodePostal"
              value={formData.adresseCodePostal}
              onChange={handleChange}
              placeholder="Code postal"
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <input
              type="text"
              name="adresseVille"
              value={formData.adresseVille}
              onChange={handleChange}
              placeholder="Ville"
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre de lots
            </label>
            <input
              type="number"
              name="nombreLots"
              value={formData.nombreLots}
              onChange={handleChange}
              min="1"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Total tantièmes
            </label>
            <input
              type="number"
              name="tantiemesTotal"
              value={formData.tantiemesTotal}
              onChange={handleChange}
              min="1"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type de construction
            </label>
            <select
              name="typeConstruction"
              value={formData.typeConstruction}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {TYPES_CONSTRUCTION.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date de réception des travaux
            </label>
            <input
              type="date"
              name="dateReceptionTravaux"
              value={formData.dateReceptionTravaux}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Les dates de garanties seront calculées automatiquement
            </p>
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {saving ? 'Enregistrement...' : (isCreate ? 'Créer la copropriété' : 'Enregistrer')}
          </button>
          {!isCreate && (
            <button
              type="button"
              onClick={() => {
                setEditMode(false);
                loadCopropriete();
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Annuler
            </button>
          )}
        </div>
      </form>
    );
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  // Mode création
  if (isCreating) {
    return (
      <AdminLayout>
        <div className="max-w-2xl mx-auto space-y-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Créer votre copropriété
            </h1>
            <p className="text-gray-500 mt-1">
              Renseignez les informations de votre copropriété pour commencer
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">{error}</p>
            </div>
          )}
          
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-green-800">{success}</p>
            </div>
          )}

          <div className="bg-white rounded-lg shadow p-6">
            {renderForm(true)}
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Mode affichage/édition
  const statutGaranties = copropriete ? getStatutGaranties(copropriete) : null;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* En-tête */}
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">
            Fiche Copropriété
          </h1>
          {!editMode && (
            <button
              onClick={() => setEditMode(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Modifier
            </button>
          )}
        </div>

        {/* Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-800">{success}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Informations générales */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Informations générales
            </h2>

            {editMode ? (
              renderForm(false)
            ) : (
              <dl className="space-y-3">
                <div className="flex justify-between py-2 border-b">
                  <dt className="text-gray-500">Nom</dt>
                  <dd className="font-medium text-gray-900">{copropriete?.nom || 'Non renseigné'}</dd>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <dt className="text-gray-500">Adresse</dt>
                  <dd className="font-medium text-gray-900 text-right">
                    {copropriete?.adresse?.rue ? (
                      <>
                        {copropriete.adresse.rue}<br />
                        {copropriete.adresse.codePostal} {copropriete.adresse.ville}
                      </>
                    ) : (
                      'Non renseignée'
                    )}
                  </dd>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <dt className="text-gray-500">Nombre de lots</dt>
                  <dd className="font-medium text-gray-900">{copropriete?.nombreLots || 'Non renseigné'}</dd>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <dt className="text-gray-500">Total tantièmes</dt>
                  <dd className="font-medium text-gray-900">
                    {copropriete?.tantiemesTotal?.toLocaleString('fr-FR') || 'Non renseigné'}
                  </dd>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <dt className="text-gray-500">Type</dt>
                  <dd className="font-medium text-gray-900">
                    {TYPES_CONSTRUCTION.find(t => t.value === copropriete?.typeConstruction)?.label || 'Non renseigné'}
                  </dd>
                </div>
                <div className="flex justify-between py-2">
                  <dt className="text-gray-500">Réception travaux</dt>
                  <dd className="font-medium text-gray-900">
                    {formatDate(copropriete?.dateReceptionTravaux)}
                  </dd>
                </div>
              </dl>
            )}
          </div>

          {/* Statut des garanties */}
          {statutGaranties && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Garanties Construction
              </h2>
              <div className="space-y-3">
                {renderGarantieStatus(
                  'Parfait Achèvement (GPA)',
                  statutGaranties.gpa.active,
                  statutGaranties.gpa.joursRestants,
                  copropriete?.dateExpirationGPA
                )}
                {renderGarantieStatus(
                  'Biennale',
                  statutGaranties.biennale.active,
                  statutGaranties.biennale.joursRestants,
                  copropriete?.dateExpirationBiennale
                )}
                {renderGarantieStatus(
                  'Décennale',
                  statutGaranties.decennale.active,
                  statutGaranties.decennale.joursRestants,
                  copropriete?.dateExpirationDecennale
                )}
              </div>

              {copropriete && 
                (copropriete.typeConstruction === 'neuf_vefa' || 
                 copropriete.typeConstruction === 'neuf_ccmi') && 
                statutGaranties.gpa.active && 
                statutGaranties.gpa.joursRestants !== null &&
                statutGaranties.gpa.joursRestants <= 90 && (
                <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm text-orange-800">
                    <strong>Attention :</strong> La garantie de parfait achèvement expire bientôt. 
                    Assurez-vous d'avoir signalé tous les désordres avant cette date.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}